import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface PlatformTitleProps {
  title: string;
  description: string;
  backButtonText?: string;
  backButtonPath?: string;
}

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid #bae6fd;
  min-height: 120px;
  position: relative;
  
  @media (max-width: 768px) {
    min-height: 120px;
    padding: 20px 15px;
  }
  
  @media (max-width: 480px) {
    min-height: 100px;
    padding: 15px 12px;
  }
`;

const BackButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  white-space: nowrap;
  
  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 13px;
    padding: 8px 12px;
  }
`;

const TitleContent = styled.div`
  text-align: center;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 80px;
  box-sizing: border-box;
  width: 100%;
  
  @media (max-width: 768px) {
    min-height: 80px;
  }
  
  @media (max-width: 480px) {
    min-height: 70px;
  }
`;

const TitleHeading = styled.h1`
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: #1e40af;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const TitleDescription = styled.p`
  margin: 0;
  font-size: 16px;
  color: #475569;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const BackButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    padding: 10px 15px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    margin-bottom: 10px;
  }
`;

const PlatformTitle: React.FC<PlatformTitleProps> = ({
  title,
  description,
  backButtonText = '← Back to Home',
  backButtonPath = '/'
}) => {
  const navigate = useNavigate();

  return (
    <>
      {/* Back Button Container - Separate box on top */}
      <BackButtonContainer>
        <BackButton onClick={() => navigate(backButtonPath)}>
          {backButtonText}
        </BackButton>
      </BackButtonContainer>

      {/* Title Container - Separate box below */}
      <TitleContainer>
        {/* Title and Description - Centered */}
        <TitleContent>
          <TitleHeading>{title}</TitleHeading>
          <TitleDescription>{description}</TitleDescription>
        </TitleContent>
      </TitleContainer>
    </>
  );
};

export default PlatformTitle;
